import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const Stat = ({ label, value, testid }) => (
  <div className="card-surface p-4" data-testid={testid}><div className="text-xs font-semibold text-muted-foreground">{label}</div><div className="text-3xl font-extrabold">{value}</div></div>
);

export const WaitlistAdmin = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const full = user.platform_role === "admin";
  const { data } = useQuery({ queryKey: ["admin-waitlist"], queryFn: () => api.get("/admin/waitlist").then((r) => r.data) });
  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/waitlist.csv", { responseType: "blob" });
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: "liste-attente-cashmy.csv" });
      a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/waitlist/${id}`); qc.invalidateQueries({ queryKey: ["admin-waitlist"] }); toast.success("Inscription supprimée"); } catch (e) { toast.error(formatApiError(e)); } };
  if (!data) return <div className="h-48 animate-pulse rounded-2xl bg-muted" />;
  return (
    <div className="space-y-6" data-testid="admin-waitlist">
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Inscrits" value={data.total} testid="waitlist-total" />
        <Stat label="Cagnottes" value={data.counts.cagnotte} testid="waitlist-count-cagnotte" />
        <Stat label="Compte en ligne + carte" value={data.counts.compte} testid="waitlist-count-compte" />
        <Stat label="Les deux" value={data.both} testid="waitlist-count-both" />
      </div>
      <section className="card-surface space-y-3 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-extrabold">Inscriptions</h2>
          {full && <Button size="sm" variant="outline" className="rounded-full" onClick={exportCsv} disabled={!data.total} data-testid="waitlist-export"><Download className="mr-1 h-4 w-4" /> Exporter en CSV</Button>}
        </div>
        {data.masked && <p className="text-xs text-muted-foreground">Les inscriptions réelles ne sont pas visibles depuis le compte de démonstration.</p>}
        {data.entries.length ? <ul className="divide-y divide-border text-sm" data-testid="waitlist-entries">{data.entries.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center gap-2 py-2" data-testid={`waitlist-entry-${e.id}`}>
            <span className="min-w-0 flex-1 truncate font-semibold">{e.email}</span>
            {e.interests.map((i) => <span key={i} className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">{i === "compte" ? "Compte en ligne" : "Cagnotte"}</span>)}
            <span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString("fr-FR")}</span>
            {full && <ConfirmDelete title="Supprimer cette inscription ?" text={`${e.email} sera retiré de la liste (droit à l'effacement).`} testid={`waitlist-delete-${e.id}`} onConfirm={() => del(e.id)} />}
          </li>))}</ul> : <p className="text-sm text-muted-foreground">Aucune inscription pour l'instant.</p>}
      </section>
    </div>
  );
};
